// Five filter tabs; Open is the default. Filter logic is win-probability + wasOpportunity-based
// (supersedes the old wonDate/lostDate table) and computed client-side from
// baselineWinChance (0–1) + status + wasOpportunity. All four tabs return real data in Phase 1.

import {deriveOpportunityStage} from './opportunityStage';

export type OpportunityFilterTab = 'all' | 'open' | 'won' | 'lost' | 'won_unconverted';

// Tab order: All first, then Open · Won · Lost · Unconverted. All leads the row, but Open is the default
// tab selected on page load (see resolveInitialTab).
// (The `won_unconverted` key keeps its internal name; its on-screen label is "Unconverted" per the design.)
export const OPPORTUNITY_FILTER_TABS: OpportunityFilterTab[] = ['all', 'open', 'won', 'lost', 'won_unconverted'];

export const OPPORTUNITY_TAB_MESSAGE_IDS: Record<OpportunityFilterTab, string> = {
	open: 'opportunities.tab.open',
	won: 'opportunities.tab.won',
	lost: 'opportunities.tab.lost',
	won_unconverted: 'opportunities.tab.won_unconverted',
	all: 'opportunities.tab.all',
};

export const OPPORTUNITY_EMPTY_MESSAGE_IDS: Record<OpportunityFilterTab, string> = {
	open: 'opportunities.empty.open',
	won: 'opportunities.empty.won',
	lost: 'opportunities.empty.lost',
	won_unconverted: 'opportunities.empty.won_unconverted',
	all: 'opportunities.empty.all',
};

export interface OpportunityLike {
	node: {
		status: string;
		name?: string | null;
		baselineWinChance?: number | null;
		wasOpportunity?: boolean | null; // R1: persistent "was ever an opportunity" marker; scopes Won
		financialNumbers?: {baselineRevenue?: number | null} | null;
		// Extra fields consumed only by sortOpportunities (per-column sorting) and
		// buildOpportunityFilterPredicate (id-based matching against the filter panel selection).
		client?: {id?: string | null; name?: string | null} | null;
		clientContact?: {id?: string | null; firstName?: string | null; lastName?: string | null} | null;
		salesperson?: {id?: string | null; firstName?: string | null; lastName?: string | null} | null;
		projectStartYear?: number | null;
		projectStartMonth?: number | null;
		projectStartDay?: number | null;
		projectEndYear?: number | null;
		projectEndMonth?: number | null;
		projectEndDay?: number | null;
	};
}

const OPPORTUNITY_STATUS = 'OPPORTUNITY'; // === PROJECT_STATUS.OPPORTUNITY

// Win probability is stored 0–1. baselineWinChance is non-null (DB default 1.00); null coalesces to
// 1.00 (= 100%). The four states are mutually exclusive, so All = their sum and per-tab counts add up.
// Won additionally requires wasOpportunity (R1) so ordinary delivery projects at the default 1.00 —
// which were never opportunities — are NOT counted as Won.
const wp = (n: OpportunityLike['node']): number => n.baselineWinChance ?? 1;
const isOpen = (n: OpportunityLike['node']): boolean => n.status === OPPORTUNITY_STATUS && wp(n) > 0 && wp(n) < 1;
const isWon = (n: OpportunityLike['node']): boolean => wp(n) === 1 && n.status !== OPPORTUNITY_STATUS && !!n.wasOpportunity;
const isLost = (n: OpportunityLike['node']): boolean => wp(n) === 0;
const isWonUnconverted = (n: OpportunityLike['node']): boolean => wp(n) === 1 && n.status === OPPORTUNITY_STATUS;

export function filterOpportunitiesByTab<T extends OpportunityLike>(rows: T[], tab: OpportunityFilterTab): T[] {
	switch (tab) {
		case 'open':
			return rows.filter(r => isOpen(r.node));
		case 'won':
			return rows.filter(r => isWon(r.node));
		case 'lost':
			return rows.filter(r => isLost(r.node));
		case 'won_unconverted':
			return rows.filter(r => isWonUnconverted(r.node));
		case 'all':
		default:
			return rows.filter(r => isOpen(r.node) || isWon(r.node) || isLost(r.node) || isWonUnconverted(r.node));
	}
}

// The row's Opportunity Status — same predicates as the tabs. null = in no bucket (non-OPP 1–99%).
export function deriveOpportunityState(node: OpportunityLike['node']): 'open' | 'won' | 'lost' | 'won_unconverted' | null {
	if (isLost(node)) return 'lost';
	if (isWonUnconverted(node)) return 'won_unconverted';
	if (isWon(node)) return 'won';
	if (isOpen(node)) return 'open';
	return null;
}

// Free-text search over the columns a user would scan for: name, client, contact, salesperson, and the
// project ID — both the custom id (e.g. "ACME-42") and the sequential id, matched with or without the
// leading "P" (so "P1234" and "1234" both hit companyProjectId 1234). Empty/whitespace term matches
// everything. Case-insensitive substring.
export function matchesOpportunitySearch(
	node: {
		name?: string | null;
		client?: {name?: string | null} | null;
		clientContact?: {firstName?: string | null; lastName?: string | null} | null;
		salesperson?: {firstName?: string | null; lastName?: string | null} | null;
		companyProjectId?: number | null;
		customProjectId?: string | null;
	},
	term: string
): boolean {
	const q = term.trim().toLowerCase();
	if (!q) return true;
	const haystacks = [
		node.name,
		node.client?.name,
		`${node.clientContact?.firstName ?? ''} ${node.clientContact?.lastName ?? ''}`,
		`${node.salesperson?.firstName ?? ''} ${node.salesperson?.lastName ?? ''}`,
		node.customProjectId,
		node.companyProjectId != null ? `P${node.companyProjectId}` : null,
		node.companyProjectId != null ? String(node.companyProjectId) : null,
	];
	return haystacks.some(h => (h ?? '').toLowerCase().includes(q));
}

// One sort key per sortable column (every data column header sorts). The two Quote placeholders
// have no data, so they are not sortable (see sortKeyForColumn).
export type OpportunitySortKey =
	| 'name'
	| 'client'
	| 'contact'
	| 'targetValue'
	| 'winChance'
	| 'stage'
	| 'startDate'
	| 'endDate'
	| 'status'
	| 'salesperson';
export type SortOrder = 'asc' | 'desc';

const personName = (p?: {firstName?: string | null; lastName?: string | null} | null): string =>
	p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim().toLowerCase() : '';

// Sortable numeric key for a Y/M/D date (0 when absent, so undated rows group together).
const dateValue = (y?: number | null, m?: number | null, d?: number | null): number =>
	y ? y * 10000 + (m ?? 0) * 100 + (d ?? 0) : 0;

export function sortOpportunities<T extends OpportunityLike>(rows: T[], key: OpportunitySortKey, order: SortOrder): T[] {
	const dir = order === 'asc' ? 1 : -1;
	const value = (r: T): string | number => {
		const n = r.node;
		switch (key) {
			case 'name':
				return (n.name ?? '').toLowerCase();
			case 'client':
				return (n.client?.name ?? '').toLowerCase();
			case 'contact':
				return personName(n.clientContact);
			case 'salesperson':
				return personName(n.salesperson);
			case 'targetValue':
				return n.financialNumbers?.baselineRevenue ?? 0;
			case 'winChance':
			case 'stage': // Opportunity Stage derives from win chance, so it orders by the same value.
				return n.baselineWinChance ?? 1;
			case 'startDate':
				return dateValue(n.projectStartYear, n.projectStartMonth, n.projectStartDay);
			case 'endDate':
				return dateValue(n.projectEndYear, n.projectEndMonth, n.projectEndDay);
			default: // 'status'
				return deriveOpportunityState(n) ?? ''; // Open/Won/Lost/Won-Unconverted (alphabetical), '' last
		}
	};
	return [...rows].sort((a, b) => {
		const va = value(a);
		const vb = value(b);
		if (va < vb) return -1 * dir;
		if (va > vb) return 1 * dir;
		return 0;
	});
}

// The default preference scope — the global /opportunities page. Host components (e.g. the
// client-detail Opportunities tab) pass a distinct scope so their tab/sort/column
// preferences persist independently. Keys are derived from the scope so the default value
// reproduces the original 'opportunities-overview-*' keys exactly — the existing page is unchanged.
export const DEFAULT_PREFERENCE_SCOPE = 'opportunities-overview';
const tabKey = (scope: string): string => `${scope}-tab`;
const sortKey = (scope: string): string => `${scope}-sort`;

export function readSessionTab(scope: string = DEFAULT_PREFERENCE_SCOPE): OpportunityFilterTab | null {
	try {
		const raw = sessionStorage.getItem(tabKey(scope));
		return raw && (OPPORTUNITY_FILTER_TABS as string[]).includes(raw) ? (raw as OpportunityFilterTab) : null;
	} catch {
		return null;
	}
}

export function writeSessionTab(tab: OpportunityFilterTab, scope: string = DEFAULT_PREFERENCE_SCOPE): void {
	try {
		sessionStorage.setItem(tabKey(scope), tab);
	} catch {
		/* sessionStorage unavailable */
	}
}

export function readSessionSort(scope: string = DEFAULT_PREFERENCE_SCOPE): {key: OpportunitySortKey; order: SortOrder} | null {
	try {
		const raw = sessionStorage.getItem(sortKey(scope));
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function writeSessionSort(
	sort: {key: OpportunitySortKey; order: SortOrder},
	scope: string = DEFAULT_PREFERENCE_SCOPE
): void {
	try {
		sessionStorage.setItem(sortKey(scope), JSON.stringify(sort));
	} catch {
		/* sessionStorage unavailable */
	}
}

// Pure tab/sort resolution, extracted so the page component holds no branchy logic (keeps its
// coverage attainable): URL wins, then the stored session tab, then 'open' — the default tab shown when
// the page loads with no URL param or stored tab. (Order is independent of the default: 'all' still leads
// the tab row; only the initially-selected tab is Open.)
export function resolveInitialTab(
	urlTab: string | null | undefined,
	sessionTab: OpportunityFilterTab | null
): OpportunityFilterTab {
	if (urlTab && (OPPORTUNITY_FILTER_TABS as string[]).includes(urlTab)) return urlTab as OpportunityFilterTab;
	return sessionTab ?? 'open';
}

export function nextSortOrder(current: {key: OpportunitySortKey; order: SortOrder}, key: OpportunitySortKey): SortOrder {
	return current.key === key && current.order === 'desc' ? 'asc' : 'desc';
}

// The shared FILTER_V4 panel hands back its selections as `{project: {<filterType>: [values], …}}`.
// We match the six opportunity filters client-side here (keeping stage/band/state derivations single-
// sourced in the feature) rather than in shared filter_util.js. Each present, non-empty filter must
// match (AND); each is include-any within itself. Absent/empty filters pass.
export type OpportunityFilterObject = {project?: Record<string, unknown>} | null | undefined;

const asIds = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

export function buildOpportunityFilterPredicate(
	filterObj: OpportunityFilterObject
): (node: OpportunityLike['node']) => boolean {
	const sel = (filterObj && filterObj.project) || {};
	const client = asIds(sel['client']);
	const contact = asIds(sel['client_contact']);
	const salesperson = asIds(sel['opportunitySalesperson']);
	const stage = asIds(sel['opportunityStage']);
	const winProbability = asIds(sel['opportunityWinProbability']);
	const status = asIds(sel['opportunityStatus']);

	return (node: OpportunityLike['node']): boolean => {
		if (client.length && !client.includes(node.client?.id ?? '')) return false;
		if (contact.length && !contact.includes(node.clientContact?.id ?? '')) return false;
		if (salesperson.length && !salesperson.includes(node.salesperson?.id ?? '')) return false;
		if (stage.length && !stage.includes(deriveOpportunityStage(node.baselineWinChance))) return false;
		if (winProbability.length && !winProbability.includes(deriveOpportunityStage(node.baselineWinChance))) return false;
		if (status.length) {
			const state = deriveOpportunityState(node);
			if (!state || !status.includes(state)) return false;
		}
		return true;
	};
}
